<template>
  <div class="revenue-stats">
    <div class="revenue-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.key"
        :class="['tab-button', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>
    
    <div class="revenue-content">
      <div class="revenue-chart">
        <Echart ref="revenueChart" :options="chartOptions" style="height: 100%; width: 100%" />
      </div>
      
      <div class="revenue-summary">
        <div class="summary-item">
          <span class="summary-value">{{ currentRevenue }}</span>
          <span class="summary-label">{{ currentLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-value">{{ currentGrowth }}%</span>
          <span class="summary-label">{{ $t('同比增长') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { putAction } from '@/api/manage'
import * as echarts from 'echarts'

export default {
  name: 'RevenueStats',
  data() {
    return {
      activeTab: 'monthly',
      tabs: [
        { key: 'monthly', label: this.$t('月度统计') }
      ],
      loading: false,
      revenueData: {
        monthStrList: [],
        payMoneyList: [],
        waterUseList: []
      },
      url: {
        list: '/jeecg-charge/meter/waterAnalysis/waterUseAndPay'
      }
    }
  },
  computed: {
    currentRevenue() {
      const total = this.revenueData.payMoneyList.reduce((sum, val) => sum + val, 0)
      return (total / 10000).toFixed(1)
    },
    currentUnit() {
      return this.$t('万元')
    },
    currentLabel() {
      return this.$t('年度营收')
    },
    currentGrowth() {
      const lastMonth = this.revenueData.payMoneyList[this.revenueData.payMoneyList.length - 1] || 0
      const prevMonth = this.revenueData.payMoneyList[this.revenueData.payMoneyList.length - 2] || 0
      if (prevMonth > 0) {
        return (((lastMonth - prevMonth) / prevMonth) * 100).toFixed(1)
      }
      return 0
    },
    chartOptions() {
      return {
        grid: {
          top: '10%',
          left: '3%',
          right: '4%',
          bottom: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.revenueData.monthStrList,
          splitLine: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.3)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 9,
            interval: 1
          }
        },
        yAxis: [
          {
            type: 'value',
            name: this.$t('用水量(m³)'),
            nameTextStyle: {
              color: '#CEE2FF',
              fontSize: 9
            },
            axisLine: {
              lineStyle: {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            },
            axisLabel: {
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 9
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(255, 255, 255, 0.1)'
              }
            }
          },
          {
            type: 'value',
            name: this.$t('缴费(元)'),
            nameTextStyle: {
              color: '#CEE2FF',
              fontSize: 9
            },
            axisLine: {
              lineStyle: {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            },
            axisLabel: {
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 9,
              formatter: '{value}'
            },
            splitLine: {
              show: false
            }
          }
        ],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0,11,42,0.9)',
          borderColor: '#5D6A94',
          borderWidth: 1,
          borderRadius: 4,
          textStyle: {
            color: '#CEE2FF',
          }
        },
        legend: {
          data: [this.$t('用水量'), this.$t('缴费')],
          textStyle: {
            color: '#CEE2FF'
          },
          icon: 'circle',
          itemHeight: 8,
          fontSize: 9,
          right: '4%',
          top: '0%'
        },
        series: [
          {
            type: 'bar',
            name: this.$t('用水量'),
            data: this.revenueData.waterUseList,
            barMaxWidth: 14,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#00D2FF' },
                { offset: 1, color: '#3A7BD5' }
              ]),
              borderRadius: [20, 20, 0, 0]
            }
          },
          {
            type: 'line',
            yAxisIndex: 1,
            name: this.$t('缴费'),
            data: this.revenueData.payMoneyList,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            lineStyle: {
              width: 2,
              color: '#F5A623'
            },
            itemStyle: {
              color: '#F5A623',
              borderColor: '#F5A623'
            }
          }
        ]
      }
    }
  },
  watch: {
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  },
  methods: {
    init() {
      this.queryWaterUseAndPay()
    },
    queryWaterUseAndPay() {
      this.loading = true
      putAction(this.url.list, {}).then((res) => {
        if(res.success){
          this.revenueData = res.result
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.revenue-stats {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  
  .revenue-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    
    .tab-button {
      flex: 1;
      padding: 0.5rem 0.75rem;
      background: linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(255, 142, 83, 0.1));
      border: 1px solid rgba(255, 107, 107, 0.2);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &.active {
        background: linear-gradient(135deg, rgba(255, 107, 107, 0.3), rgba(255, 142, 83, 0.3));
        border-color: rgba(255, 107, 107, 0.5);
        color: #fff;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(255, 107, 107, 0.2);
      }
      
      &:hover:not(.active) {
        background: linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(255, 142, 83, 0.2));
        border-color: rgba(255, 107, 107, 0.3);
        transform: translateY(-1px);
      }
    }
  }
  
  .revenue-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .revenue-chart {
    flex: 1;
    min-height: 0;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  .revenue-summary {
    display: flex;
    gap: 0.75rem;
    
    .summary-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0.75rem;
      background: linear-gradient(135deg, rgba(255, 107, 107, 0.05), rgba(255, 142, 83, 0.05));
      border: 1px solid rgba(255, 107, 107, 0.1);
      border-radius: 8px;
      
      .summary-value {
        font-family: 'ds-digital-bold', monospace;
        font-size: 1.5rem;
        font-weight: bold;
        color: #FF6B6B;
        text-shadow: 0 0 10px rgba(255, 107, 107, 0.5);
        margin-bottom: 0.25rem;
      }
      
      .summary-label {
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.8);
        text-align: center;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .revenue-stats {
    .stats-grid {
      gap: 0.5rem;
    }
    
    .stat-item {
      padding: 0.5rem;
      gap: 0.5rem;
    }
    
    .stat-icon {
      width: 2rem;
      height: 2rem;
    }
    
    .stat-value {
      font-size: 1.125rem;
    }
    
    .stat-label {
      font-size: 0.625rem;
    }
  }
}
</style>